import { useCallback, useEffect, useState } from 'react';
import { Plus, Search, Edit2, Trash2, Eye, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { Pagination } from '../components/Pagination';
import { Modal } from '../components/Modal';
import { ProductImageGallery } from '../components/ProductImageGallery';
import { productService } from '../services/productService';
import { basalamService } from '../services/basalamService';
import { fieldClass, labelClass, slugify, formatMoney, parseNumber } from '../utils/form';
import { NumberField } from '../components/NumberField';
import { rowNumber } from '../utils/rowNumber';
import type { Category, Product } from '../types';

type ModalMode = 'create' | 'edit' | 'view' | null;

type ProductDetail = Product & {
  slug?: string;
  shortDescription?: string;
  videoUrl?: string;
  dimensions?: string | { length?: number; width?: number; height?: number };
  basalamProductId?: number | string;
  basalamPrice?: number;
  basalamPriceSynced?: boolean;
};

type ProductForm = {
  name: string;
  slug: string;
  sku: string;
  categoryId: string;
  price: string;
  costPrice: string;
  discountPrice: string;
  stock: string;
  weight: string;
  description: string;
  shortDescription: string;
  videoUrl: string;
  isActive: boolean;
  dimensions: string;
  colors: { name: string; hex: string; price: string }[];
  variants: { name: string; price: string }[];
};

const emptyForm = (): ProductForm => ({
  name: '',
  slug: '',
  sku: '',
  categoryId: '',
  price: '',
  costPrice: '',
  discountPrice: '',
  stock: '0',
  weight: '0',
  description: '',
  shortDescription: '',
  videoUrl: '',
  isActive: true,
  dimensions: '',
  colors: [],
  variants: [],
});

function dimensionsToString(value: ProductDetail['dimensions']): string {
  if (!value) return '';
  if (typeof value === 'string') return value;
  const parts = [value.length, value.width, value.height].filter((n) => n != null);
  return parts.join(' × ');
}

function productToForm(p: ProductDetail): ProductForm {
  return {
    name: p.name ?? '',
    slug: p.slug ?? '',
    sku: p.sku ?? '',
    categoryId: p.category?.id != null ? String(p.category.id) : '',
    price: p.price != null ? String(p.price) : '',
    costPrice: p.costPrice != null ? String(p.costPrice) : '0',
    discountPrice: p.discountPrice != null ? String(p.discountPrice) : '',
    stock: String(p.stock ?? 0),
    weight: String(p.weight ?? 0),
    description: p.description ?? '',
    shortDescription: p.shortDescription ?? '',
    videoUrl: p.videoUrl ?? '',
    isActive: Boolean(p.isActive),
    dimensions: dimensionsToString(p.dimensions),
    colors: Array.isArray(p.colors)
      ? p.colors
          .map((c) => ({
            name: c.name ?? '',
            hex: c.hex || '#cccccc',
            price: c.price != null && Number(c.price) > 0 ? String(c.price) : '',
          }))
          .filter((c) => c.name)
      : [],
    variants: Array.isArray(p.variants)
      ? p.variants
          .map((v) => ({
            name: v.name ?? '',
            price: v.price != null && Number(v.price) > 0 ? String(v.price) : '',
          }))
          .filter((v) => v.name)
      : [],
  };
}

function optionPricePayload(raw: string): number | undefined {
  const t = raw.trim();
  if (!t) return undefined;
  const n = parseNumber(t);
  return n > 0 ? n : undefined;
}

function buildPayload(form: ProductForm) {
  const discountRaw = form.discountPrice.trim();
  return {
    name: form.name.trim(),
    slug: form.slug.trim() || slugify(form.name),
    sku: form.sku.trim(),
    categoryId: /^\d+$/.test(form.categoryId) ? Number(form.categoryId) : form.categoryId,
    price: parseNumber(form.price),
    costPrice: parseNumber(form.costPrice),
    discountPrice: discountRaw === '' ? null : parseNumber(discountRaw),
    stock: parseNumber(form.stock),
    weight: parseNumber(form.weight),
    description: form.description.trim(),
    shortDescription: form.shortDescription.trim(),
    videoUrl: form.videoUrl.trim(),
    isActive: form.isActive,
    dimensions: form.dimensions.trim(),
    colors: form.colors
      .map((c) => {
        const price = optionPricePayload(c.price);
        return {
          name: c.name.trim(),
          hex: c.hex || undefined,
          ...(price !== undefined ? { price } : {}),
        };
      })
      .filter((c) => c.name),
    variants: form.variants
      .map((v) => {
        const price = optionPricePayload(v.price);
        return {
          name: v.name.trim(),
          ...(price !== undefined ? { price } : {}),
        };
      })
      .filter((v) => v.name),
  };
}

export function ProductsPage() {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [sortBy, setSortBy] = useState('updated');
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const itemsPerPage = 10;

  const [modal, setModal] = useState<ModalMode>(null);
  const [form, setForm] = useState<ProductForm>(emptyForm);
  const [viewProduct, setViewProduct] = useState<ProductDetail | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingBasalamId, setEditingBasalamId] = useState<string | null>(null);
  const [editingBasalamSynced, setEditingBasalamSynced] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncingPrices, setSyncingPrices] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const needsBasalamPriceSync = (p: { basalamProductId?: number | string; basalamPriceSynced?: boolean } | null | undefined) =>
    Boolean(p?.basalamProductId) && !p?.basalamPriceSynced;

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const [prodRes, cats] = await Promise.all([
        productService.getProducts({
          page: currentPage,
          limit: itemsPerPage,
          sortBy,
          q: searchTerm.trim() || undefined,
          categoryId: filterCategory || undefined,
        }),
        productService.getCategories(),
      ]);
      setProducts(prodRes.data ?? []);
      setTotalPages(Math.max(1, prodRes.totalPages));
      setCategories(cats ?? []);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در دریافت محصولات');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, sortBy, searchTerm, filterCategory]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!cancelled) await loadList();
    })();
    return () => {
      cancelled = true;
    };
  }, [loadList]);

  const filteredProducts = products;

  const closeModal = () => {
    setModal(null);
    setEditingId(null);
    setEditingBasalamId(null);
    setEditingBasalamSynced(false);
    setViewProduct(null);
    setForm(emptyForm());
    setSlugTouched(false);
    setSaving(false);
    setSyncingPrices(false);
    setDetailLoading(false);
  };

  const applySyncedPricesToForm = (p: ProductDetail) => {
    setForm((f) => ({
      ...f,
      price: p.price != null ? String(p.price) : f.price,
      colors: Array.isArray(p.colors)
        ? p.colors.map((c) => ({
            name: c.name ?? '',
            hex: c.hex || '#cccccc',
            price: c.price != null && Number(c.price) > 0 ? String(c.price) : '',
          }))
        : f.colors,
      variants: Array.isArray(p.variants)
        ? p.variants.map((v) => ({
            name: v.name ?? '',
            price: v.price != null && Number(v.price) > 0 ? String(v.price) : '',
          }))
        : f.variants,
    }));
  };

  const handleSyncBasalamPrices = async (productId: string, refreshForm: boolean) => {
    setSyncingPrices(true);
    try {
      const updated = (await basalamService.syncProductPrices(productId)) as ProductDetail;
      toast.success(
        `قیمت از باسلام به‌روز شد: ${formatMoney(Number(updated.price) || 0)} تومان`,
      );
      if (refreshForm) {
        applySyncedPricesToForm(updated);
        if (updated.basalamProductId != null) {
          setEditingBasalamId(String(updated.basalamProductId));
        }
        setEditingBasalamSynced(Boolean(updated.basalamPriceSynced ?? true));
      }
      await loadList();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'به‌روزرسانی قیمت از باسلام ناموفق بود');
    } finally {
      setSyncingPrices(false);
    }
  };

  const openCreate = () => {
    setForm(emptyForm());
    setEditingId(null);
    setSlugTouched(false);
    setModal('create');
  };

  const openView = async (id: string) => {
    setModal('view');
    setViewProduct(null);
    setDetailLoading(true);
    try {
      const p = (await productService.getProduct(id)) as ProductDetail;
      setViewProduct(p);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در دریافت محصول');
      setModal(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const openEdit = async (id: string) => {
    setModal('edit');
    setEditingId(id);
    setEditingBasalamId(null);
    setEditingBasalamSynced(false);
    setDetailLoading(true);
    setSlugTouched(true);
    try {
      const p = (await productService.getProduct(id)) as ProductDetail;
      setForm(productToForm(p));
      setEditingBasalamId(
        p.basalamProductId != null && String(p.basalamProductId) !== ''
          ? String(p.basalamProductId)
          : null,
      );
      setEditingBasalamSynced(Boolean(p.basalamPriceSynced));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در دریافت محصول');
      setModal(null);
      setEditingId(null);
      setEditingBasalamId(null);
      setEditingBasalamSynced(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleNameChange = (name: string) => {
    setForm((f) => ({
      ...f,
      name,
      slug: modal === 'create' && !slugTouched ? slugify(name) : f.slug,
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('نام محصول را وارد کنید');
      return;
    }
    if (!form.sku.trim()) {
      toast.error('SKU را وارد کنید');
      return;
    }
    if (!form.categoryId) {
      toast.error('دسته‌بندی را انتخاب کنید');
      return;
    }

    setSaving(true);
    try {
      const payload = buildPayload(form);
      if (modal === 'edit' && editingId) {
        await productService.updateProduct(editingId, payload);
        toast.success('محصول به‌روزرسانی شد');
        closeModal();
        await loadList();
      } else {
        const created = await productService.createProduct(payload);
        toast.success('محصول ایجاد شد — حالا می‌توانید گالری را اضافه کنید');
        await loadList();
        // Switch to edit so gallery upload is available immediately
        setEditingId(created.id);
        setViewProduct(created as ProductDetail);
        setForm(productToForm(created as ProductDetail));
        setSlugTouched(true);
        setModal('edit');
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ذخیره ناموفق بود');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('آیا از حذف این محصول مطمئن هستید؟')) return;
    try {
      await productService.deleteProduct(id);
      toast.success('محصول حذف شد');
      await loadList();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'حذف ناموفق بود');
    }
  };

  const formFields = (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" dir="rtl">
      <div>
        <label className={labelClass}>نام محصول</label>
        <input
          className={fieldClass}
          value={form.name}
          onChange={(e) => handleNameChange(e.target.value)}
          dir="rtl"
        />
      </div>
      <div>
        <label className={labelClass}>Slug</label>
        <input
          className={fieldClass}
          value={form.slug}
          onChange={(e) => {
            setSlugTouched(true);
            setForm((f) => ({ ...f, slug: e.target.value }));
          }}
          dir="ltr"
        />
      </div>
      <div>
        <label className={labelClass}>SKU</label>
        <input
          className={fieldClass}
          value={form.sku}
          onChange={(e) => setForm((f) => ({ ...f, sku: e.target.value }))}
          dir="ltr"
        />
      </div>
      <div>
        <label className={labelClass}>دسته‌بندی</label>
        <select
          className={fieldClass}
          value={form.categoryId}
          onChange={(e) => setForm((f) => ({ ...f, categoryId: e.target.value }))}
        >
          <option value="">انتخاب دسته</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>
      <NumberField
        label="قیمت (تومان)"
        value={form.price}
        onChange={(v) => setForm((f) => ({ ...f, price: v }))}
      />
      <NumberField
        label="قیمت تمام‌شده (تومان)"
        value={form.costPrice}
        onChange={(v) => setForm((f) => ({ ...f, costPrice: v }))}
        hint="فقط در پنل ادمین — برای محاسبه سود؛ در سایت مشتری نمایش داده نمی‌شود."
      />
      <NumberField
        label="قیمت با تخفیف"
        value={form.discountPrice}
        onChange={(v) => setForm((f) => ({ ...f, discountPrice: v }))}
        placeholder="خالی = بدون تخفیف"
      />
      <NumberField
        label="موجودی"
        value={form.stock}
        onChange={(v) => setForm((f) => ({ ...f, stock: v }))}
      />
      <NumberField
        label="وزن (گرم)"
        value={form.weight}
        onChange={(v) => setForm((f) => ({ ...f, weight: v }))}
      />
      <div className="md:col-span-2">
        <label className={labelClass}>ابعاد</label>
        <input
          className={fieldClass}
          value={form.dimensions}
          onChange={(e) => setForm((f) => ({ ...f, dimensions: e.target.value }))}
          placeholder="مثلاً ۱۰ × ۵ × ۳"
          dir="rtl"
        />
      </div>
      <div className="md:col-span-2 space-y-2 rounded-lg border border-slate-200 p-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className={labelClass}>رنگبندی (اختیاری)</p>
            <p className="text-xs text-slate-500">اگر رنگی اضافه شود، مشتری باید یکی را انتخاب کند.</p>
          </div>
          <button
            type="button"
            className="text-sm text-indigo-600 hover:text-indigo-800"
            onClick={() =>
              setForm((f) => ({ ...f, colors: [...f.colors, { name: '', hex: '#dc2626', price: '' }] }))
            }
          >
            + افزودن رنگ
          </button>
        </div>
        {form.colors.map((c, i) => (
          <div key={i} className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <input
              type="color"
              value={c.hex || '#cccccc'}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  colors: f.colors.map((row, idx) => (idx === i ? { ...row, hex: e.target.value } : row)),
                }))
              }
              className="h-10 w-12 cursor-pointer rounded border border-slate-300 bg-white p-1"
            />
            <input
              className={fieldClass}
              value={c.name}
              placeholder="مثلاً قرمز"
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  colors: f.colors.map((row, idx) => (idx === i ? { ...row, name: e.target.value } : row)),
                }))
              }
              dir="rtl"
            />
            <NumberField
              value={c.price}
              onChange={(v) =>
                setForm((f) => ({
                  ...f,
                  colors: f.colors.map((row, idx) => (idx === i ? { ...row, price: v } : row)),
                }))
              }
              placeholder="قیمت اختصاصی (اختیاری)"
            />
            <button
              type="button"
              className="text-sm text-red-600 shrink-0"
              onClick={() => setForm((f) => ({ ...f, colors: f.colors.filter((_, idx) => idx !== i) }))}
            >
              حذف
            </button>
          </div>
        ))}
      </div>
      <div className="md:col-span-2 space-y-2 rounded-lg border border-slate-200 p-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className={labelClass}>تنوع (اختیاری)</p>
            <p className="text-xs text-slate-500">
              اگر تنوع اضافه شود، مشتری باید یکی را انتخاب کند. قیمت اختصاصی اولویت دارد بر قیمت رنگ و محصول.
            </p>
          </div>
          <button
            type="button"
            className="text-sm text-indigo-600 hover:text-indigo-800"
            onClick={() => setForm((f) => ({ ...f, variants: [...f.variants, { name: '', price: '' }] }))}
          >
            + افزودن تنوع
          </button>
        </div>
        {form.variants.map((v, i) => (
          <div key={i} className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <input
              className={fieldClass}
              value={v.name}
              placeholder="مثلاً ۲۰ شاخه"
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  variants: f.variants.map((row, idx) =>
                    idx === i ? { ...row, name: e.target.value } : row,
                  ),
                }))
              }
              dir="rtl"
            />
            <NumberField
              value={v.price}
              onChange={(val) =>
                setForm((f) => ({
                  ...f,
                  variants: f.variants.map((row, idx) => (idx === i ? { ...row, price: val } : row)),
                }))
              }
              placeholder="قیمت اختصاصی (اختیاری)"
            />
            <button
              type="button"
              className="text-sm text-red-600 shrink-0"
              onClick={() => setForm((f) => ({ ...f, variants: f.variants.filter((_, idx) => idx !== i) }))}
            >
              حذف
            </button>
          </div>
        ))}
      </div>
      <div className="md:col-span-2">
        <label className={labelClass}>توضیح کوتاه</label>
        <textarea
          className={fieldClass}
          rows={2}
          value={form.shortDescription}
          onChange={(e) => setForm((f) => ({ ...f, shortDescription: e.target.value }))}
          dir="rtl"
        />
      </div>
      <div className="md:col-span-2">
        <label className={labelClass}>توضیحات</label>
        <textarea
          className={fieldClass}
          rows={4}
          value={form.description}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          dir="rtl"
        />
      </div>
      <div className="md:col-span-2">
        <label className={labelClass}>ویدیو آپارات (اختیاری)</label>
        <input
          className={fieldClass}
          value={form.videoUrl}
          onChange={(e) => setForm((f) => ({ ...f, videoUrl: e.target.value }))}
          placeholder="https://www.aparat.com/v/xxxxx یا کد امبد"
          dir="ltr"
        />
        <p className="mt-1 text-xs text-slate-500">
          لینک صفحه ویدیو یا کد امبد آپارات را بگذارید. خالی = بدون ویدیو.
        </p>
      </div>
      <div className="md:col-span-2 flex items-center gap-2">
        <input
          id="product-active"
          type="checkbox"
          checked={form.isActive}
          onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
          className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
        />
        <label htmlFor="product-active" className="text-sm font-medium text-slate-700">
          فعال
        </label>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="page-title text-xl font-bold text-slate-900 sm:text-3xl">محصولات</h1>
          <p className="text-slate-600 mt-1">مدیریت محصولات ایوا</p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="w-full sm:w-auto flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors font-medium"
        >
          <Plus className="h-4 w-4" />
          افزودن محصول
        </button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="جستجو به نام یا SKU..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pr-10 pl-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-right"
              dir="rtl"
            />
          </div>

          <select
            value={filterCategory}
            onChange={(e) => {
              setFilterCategory(e.target.value);
              setCurrentPage(1);
            }}
            className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">تمام دسته‌بندی‌ها</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>

          <select
            value={sortBy}
            onChange={(e) => {
              setCurrentPage(1);
              setSortBy(e.target.value);
            }}
            className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="updated">آخرین بروزرسانی</option>
            <option value="most_views">بیشترین مشاهده</option>
            <option value="least_views">کمترین مشاهده</option>
            <option value="most_cart">بیشترین سبد خرید</option>
            <option value="least_cart">کمترین سبد خرید</option>
            <option value="newest">جدیدترین</option>
            <option value="oldest">قدیمی‌ترین</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="admin-table-scroll">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">ردیف</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">نام محصول</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">SKU</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">قیمت</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">موجودی</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">بازدید</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">سبد</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">دسته</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">وضعیت</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">عملیات</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} className="px-6 py-8 text-center text-slate-500">
                    در حال بارگذاری...
                  </td>
                </tr>
              ) : filteredProducts.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-6 py-8 text-center text-slate-500">
                    محصولی یافت نشد
                  </td>
                </tr>
              ) : (
                filteredProducts.map((product, index) => (
                  <tr
                    key={product.id}
                    className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                  >
                    <td className="px-6 py-4 text-sm tabular-nums text-slate-500">
                      {rowNumber(index, currentPage, itemsPerPage)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center overflow-hidden">
                          {product.mainImage?.thumbnailUrl || product.mainImage?.url ? (
                            <img
                              src={product.mainImage.thumbnailUrl || product.mainImage.url}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            '🌸'
                          )}
                        </div>
                        <p className="text-sm font-medium text-slate-900">{product.name}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">{product.sku}</td>
                    <td className="px-6 py-4 text-sm font-medium text-slate-900">
                      {formatMoney(product.price || 0)} تومان
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-block px-3 py-1 text-xs rounded-full font-medium ${
                          (product.availableStock ?? product.stock) > 10
                            ? 'bg-green-100 text-green-700'
                            : (product.availableStock ?? product.stock) > 0
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {(product.availableStock ?? product.stock)} عدد
                        {product.reservedStock && product.reservedStock > 0
                          ? ` (رزرو: ${product.reservedStock})`
                          : ''}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm tabular-nums text-slate-600">
                      {(product.viewCount ?? 0).toLocaleString('fa-IR')}
                    </td>
                    <td className="px-6 py-4 text-sm tabular-nums text-slate-600">
                      {(product.cartCount ?? 0).toLocaleString('fa-IR')}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">{product.category?.name ?? '—'}</td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-block px-3 py-1 text-xs rounded-full font-medium ${
                          product.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {product.isActive ? 'فعال' : 'غیرفعال'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          className="text-indigo-600 hover:text-indigo-700 p-1"
                          type="button"
                          onClick={() => void openView(product.id)}
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          className="text-blue-600 hover:text-blue-700 p-1"
                          type="button"
                          onClick={() => void openEdit(product.id)}
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        {needsBasalamPriceSync(product) ? (
                          <button
                            className="text-amber-600 hover:text-amber-700 p-1 disabled:opacity-50"
                            type="button"
                            title="به‌روزرسانی قیمت از باسلام"
                            disabled={syncingPrices}
                            onClick={() => void handleSyncBasalamPrices(product.id, false)}
                          >
                            <RefreshCw className={`h-4 w-4 ${syncingPrices ? 'animate-spin' : ''}`} />
                          </button>
                        ) : null}
                        <button
                          className="text-red-600 hover:text-red-700 p-1"
                          type="button"
                          onClick={() => void handleDelete(product.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-center">
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
      </div>

      <Modal
        isOpen={modal === 'create' || modal === 'edit'}
        onClose={closeModal}
        title={modal === 'edit' ? 'ویرایش محصول' : 'افزودن محصول'}
        size="xl"
        footer={
          <>
            {modal === 'edit' && editingId && editingBasalamId && !editingBasalamSynced ? (
              <button
                type="button"
                onClick={() => void handleSyncBasalamPrices(editingId, true)}
                disabled={saving || detailLoading || syncingPrices}
                className="px-4 py-2 rounded-lg border border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 disabled:opacity-50 inline-flex items-center gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${syncingPrices ? 'animate-spin' : ''}`} />
                {syncingPrices ? 'در حال همگام‌سازی...' : 'به‌روزرسانی قیمت از باسلام'}
              </button>
            ) : null}
            <button
              type="button"
              onClick={closeModal}
              className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
            >
              انصراف
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving || detailLoading || syncingPrices}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'در حال ذخیره...' : 'ذخیره'}
            </button>
          </>
        }
      >
        {detailLoading && modal === 'edit' ? (
          <p className="text-center text-slate-500 py-8">در حال بارگذاری...</p>
        ) : (
          <div className="space-y-4">
            {modal === 'edit' && editingBasalamId ? (
              <p
                className={`text-xs rounded-lg px-3 py-2 border ${
                  editingBasalamSynced
                    ? 'text-emerald-800 bg-emerald-50 border-emerald-200'
                    : 'text-amber-800 bg-amber-50 border-amber-200'
                }`}
              >
                {editingBasalamSynced
                  ? `قیمت این محصول با باسلام (ID: ${editingBasalamId}) همگام است.`
                  : `این محصول به باسلام (ID: ${editingBasalamId}) وصل است و قیمتش با باسلام فرق دارد. با دکمه «به‌روزرسانی قیمت از باسلام» قیمت اصلی و قیمت تنوع/رنگ‌ها همگام می‌شود.`}
              </p>
            ) : null}
            {formFields}
            {modal === 'edit' && editingId ? (
              <ProductImageGallery productId={editingId} />
            ) : (
              <p className="text-xs text-slate-500 border border-dashed border-slate-200 rounded-lg p-3">
                پس از ذخیره محصول، می‌توانید چند تصویر به گالری اضافه کنید.
              </p>
            )}
          </div>
        )}
      </Modal>

      <Modal
        isOpen={modal === 'view'}
        onClose={closeModal}
        title="جزئیات محصول"
        size="lg"
        footer={
          <button
            type="button"
            onClick={closeModal}
            className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            بستن
          </button>
        }
      >
        {detailLoading || !viewProduct ? (
          <p className="text-center text-slate-500 py-8">در حال بارگذاری...</p>
        ) : (
          <div className="space-y-4 text-right" dir="rtl">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-lg bg-slate-100 overflow-hidden flex items-center justify-center">
                {viewProduct.mainImage?.thumbnailUrl || viewProduct.mainImage?.url ? (
                  <img
                    src={viewProduct.mainImage.thumbnailUrl || viewProduct.mainImage.url}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : (
                  '🌸'
                )}
              </div>
              <div>
                <p className="text-lg font-semibold text-slate-900">{viewProduct.name}</p>
                <p className="text-sm text-slate-500">{viewProduct.sku}</p>
              </div>
            </div>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <dt className="text-slate-500">Slug</dt>
                <dd className="font-medium text-slate-900" dir="ltr">
                  {viewProduct.slug || '—'}
                </dd>
              </div>
              <div>
                <dt className="text-slate-500">دسته</dt>
                <dd className="font-medium text-slate-900">{viewProduct.category?.name ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-slate-500">قیمت</dt>
                <dd className="font-medium text-slate-900">{formatMoney(viewProduct.price || 0)} تومان</dd>
              </div>
              <div>
                <dt className="text-slate-500">قیمت تمام‌شده</dt>
                <dd className="font-medium text-slate-900">{formatMoney(viewProduct.costPrice || 0)} تومان</dd>
              </div>
              <div>
                <dt className="text-slate-500">قیمت با تخفیف</dt>
                <dd className="font-medium text-slate-900">
                  {viewProduct.discountPrice != null
                    ? `${formatMoney(viewProduct.discountPrice)} تومان`
                    : '—'}
                </dd>
              </div>
              <div>
                <dt className="text-slate-500">موجودی</dt>
                <dd className="font-medium text-slate-900">
                  {(viewProduct.availableStock ?? viewProduct.stock)} عدد
                  {viewProduct.reservedStock && viewProduct.reservedStock > 0
                    ? ` (رزرو پرداخت‌نشده: ${viewProduct.reservedStock})`
                    : ''}
                </dd>
              </div>
              <div>
                <dt className="text-slate-500">بازدید</dt>
                <dd className="font-medium text-slate-900">
                  {(viewProduct.viewCount ?? 0).toLocaleString('fa-IR')}
                </dd>
              </div>
              <div>
                <dt className="text-slate-500">وزن</dt>
                <dd className="font-medium text-slate-900">{viewProduct.weight ?? 0} گرم</dd>
              </div>
              <div>
                <dt className="text-slate-500">ابعاد</dt>
                <dd className="font-medium text-slate-900">
                  {dimensionsToString(viewProduct.dimensions) || '—'}
                </dd>
              </div>
              <div>
                <dt className="text-slate-500">وضعیت</dt>
                <dd className="font-medium text-slate-900">{viewProduct.isActive ? 'فعال' : 'غیرفعال'}</dd>
              </div>
              <div className="md:col-span-2">
                <dt className="text-slate-500">رنگبندی</dt>
                <dd className="font-medium text-slate-900">
                  {viewProduct.colors?.length
                    ? viewProduct.colors.map((c) => c.name).join('، ')
                    : '—'}
                </dd>
              </div>
              <div className="md:col-span-2">
                <dt className="text-slate-500">تنوع</dt>
                <dd className="font-medium text-slate-900">
                  {viewProduct.variants?.length
                    ? viewProduct.variants.map((v) => v.name).join('، ')
                    : '—'}
                </dd>
              </div>
              <div className="md:col-span-2">
                <dt className="text-slate-500">توضیح کوتاه</dt>
                <dd className="font-medium text-slate-900">{viewProduct.shortDescription || '—'}</dd>
                <dt className="text-slate-500 mt-3">ویدیو آپارات</dt>
                <dd className="font-medium text-slate-900 break-all" dir="ltr">
                  {(viewProduct as ProductDetail).videoUrl || '—'}
                </dd>
              </div>
              <div className="md:col-span-2">
                <dt className="text-slate-500">توضیحات</dt>
                <dd className="font-medium text-slate-900 whitespace-pre-wrap">
                  {viewProduct.description || '—'}
                </dd>
              </div>
            </dl>
            {viewProduct.id ? (
              <ProductImageGallery productId={String(viewProduct.id)} readOnly />
            ) : null}
          </div>
        )}
      </Modal>
    </div>
  );
}
